// Actividades (§ 1.18, 3.3): pestañas de Tareas, Llamadas y Reuniones con
// creación y completar/registrar.
import type { JSX } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api.ts";
import type { Row } from "../lib/resource.ts";
import { formatDate, formatDateTime } from "../lib/format.ts";
import { Badge, Button, Card, EmptyState, Field, Input, Loading, Modal, Select, Textarea } from "../components/ui.tsx";
import { AsyncCombobox } from "../components/AsyncCombobox.tsx";
import { toast } from "../components/toast.tsx";

type Tab = "tasks" | "calls" | "meetings";
const PRIORITY = ["Alta", "Media", "Baja"] as const;
const TASK_STATUS = ["No iniciada", "En progreso", "Completada", "Aplazada", "En espera"] as const;
const CALL_TYPE = ["Entrante", "Saliente"] as const;
const PRIO_COLOR: Record<string, string> = { Alta: "red", Media: "amber", Baja: "gray" };

export function Activities(): JSX.Element {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("tasks");
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-ink">Actividades</h1>
        <div className="flex gap-2">
          <Button onClick={() => navigate("/activities/calendar")}>Calendario</Button>
          <Button variant="primary" onClick={() => setCreating(true)}>+ Nueva</Button>
        </div>
      </div>
      <div className="flex gap-1 border-b border-line">
        {(["tasks", "calls", "meetings"] as Tab[]).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === t ? "border-primary text-primary font-medium" : "border-transparent text-ink-secondary hover:text-ink"}`}>
            {t === "tasks" ? "Tareas" : t === "calls" ? "Llamadas" : "Reuniones"}
          </button>
        ))}
      </div>
      {tab === "tasks" && <TasksTab />}
      {tab === "calls" && <CallsTab />}
      {tab === "meetings" && <MeetingsTab />}
      {creating && <NewActivityModal tab={tab} onClose={() => setCreating(false)} />}
    </div>
  );
}

function TasksTab(): JSX.Element {
  const qc = useQueryClient();
  const tasks = useQuery<{ rows: Row[] }>({ queryKey: ["list", "tasks"], queryFn: () => api.get("/tasks") });
  const complete = useMutation({
    mutationFn: (id: string) => api.patch(`/tasks/${id}`, { status: "Completada" }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["list", "tasks"] }); toast.success("Tarea completada."); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo actualizar."),
  });
  if (tasks.isLoading) return <Loading />;
  const rows = tasks.data?.rows ?? [];
  if (rows.length === 0) return <EmptyState title="Sin tareas" body="Crea la primera tarea." />;
  return (
    <Card>
      <table className="w-full">
        <thead><tr className="bg-canvas"><th className="th"></th><th className="th">Asunto</th><th className="th">Vence</th><th className="th">Prioridad</th><th className="th">Estado</th></tr></thead>
        <tbody>
          {rows.map((t) => {
            const done = t["status"] === "Completada";
            return (
              <tr key={String(t["id"])}>
                <td className="td"><input type="checkbox" checked={done} disabled={done} onChange={() => complete.mutate(String(t["id"]))} /></td>
                <td className={`td font-medium ${done ? "line-through text-ink-muted" : ""}`}>{String(t["subject"])}</td>
                <td className="td">{formatDate(t["dueDate"] as string)}</td>
                <td className="td"><Badge color={PRIO_COLOR[String(t["priority"])] ?? "gray"}>{String(t["priority"])}</Badge></td>
                <td className="td text-ink-secondary">{String(t["status"])}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

function CallsTab(): JSX.Element {
  const calls = useQuery<{ rows: Row[] }>({ queryKey: ["list", "calls"], queryFn: () => api.get("/calls") });
  if (calls.isLoading) return <Loading />;
  const rows = calls.data?.rows ?? [];
  if (rows.length === 0) return <EmptyState title="Sin llamadas" body="Registra o programa una llamada." />;
  return (
    <Card>
      <table className="w-full">
        <thead><tr className="bg-canvas"><th className="th">Asunto</th><th className="th">Tipo</th><th className="th">Inicio</th><th className="th">Estado</th></tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={String(c["id"])}>
              <td className="td font-medium">{String(c["subject"])}</td>
              <td className="td">{String(c["callType"])}</td>
              <td className="td">{formatDateTime(c["startTime"] as string)}</td>
              <td className="td text-ink-secondary">{String(c["callStatus"])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function MeetingsTab(): JSX.Element {
  const meetings = useQuery<{ rows: Row[] }>({ queryKey: ["list", "meetings"], queryFn: () => api.get("/meetings") });
  if (meetings.isLoading) return <Loading />;
  const rows = meetings.data?.rows ?? [];
  if (rows.length === 0) return <EmptyState title="Sin reuniones" body="Agenda una reunión." />;
  return (
    <Card>
      <table className="w-full">
        <thead><tr className="bg-canvas"><th className="th">Título</th><th className="th">Inicio</th><th className="th">Fin</th><th className="th">Lugar</th></tr></thead>
        <tbody>
          {rows.map((m) => (
            <tr key={String(m["id"])}>
              <td className="td font-medium">{String(m["title"])}</td>
              <td className="td">{formatDateTime(m["startAt"] as string)}</td>
              <td className="td">{formatDateTime(m["endAt"] as string)}</td>
              <td className="td">{String(m["location"] ?? "—")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function NewActivityModal({ tab, onClose }: { tab: Tab; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  // tareas
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState<string>("Media");
  const [status, setStatus] = useState<string>("No iniciada");
  // llamadas / reuniones
  const [callType, setCallType] = useState<string>("Saliente");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [isOnline, setIsOnline] = useState(false);
  // relación (opcional): cuenta o contacto
  const [relType, setRelType] = useState<"accounts" | "contacts">("accounts");
  const [relId, setRelId] = useState("");
  const [relAccountId, setRelAccountId] = useState<string | undefined>();
  const [relAccountName, setRelAccountName] = useState("");

  const mut = useMutation({
    mutationFn: (payload: { path: string; body: Row }) => api.post<Record<string, Row>>(payload.path, payload.body),
    onSuccess: (data) => {
      const key = tab === "tasks" ? "tasks" : tab === "calls" ? "calls" : "meetings";
      void qc.invalidateQueries({ queryKey: ["list", key] });
      const url = (data?.["meeting"] as Row | undefined)?.["onlineMeetingUrl"];
      if (url) toast.success("Reunión creada en Outlook con enlace de Teams.");
      else toast.success("Actividad creada.");
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo crear."),
  });

  const submit = (): void => {
    if (!subject.trim()) { toast.error("El asunto es obligatorio."); return; }
    // Relación opcional. Si es un contacto, se vincula también su cuenta.
    const rel: Row = relId
      ? { relatedModule: relType, relatedId: relId, ...(relAccountId ? { accountId: relAccountId } : {}) }
      : {};
    if (tab === "tasks") {
      const body: Row = { subject, priority, status, ...rel };
      if (dueDate) body["dueDate"] = dueDate;
      if (description) body["description"] = description;
      mut.mutate({ path: "/tasks", body });
    } else if (tab === "calls") {
      if (!startTime) { toast.error("La hora de inicio es obligatoria."); return; }
      const body: Row = { subject, callType, startTime: new Date(startTime).toISOString(), ...rel };
      if (description) body["description"] = description;
      mut.mutate({ path: "/calls/log", body });
    } else {
      if (!startTime || !endTime) { toast.error("Inicio y fin son obligatorios."); return; }
      mut.mutate({ path: "/meetings", body: { title: subject, startAt: new Date(startTime).toISOString(), endAt: new Date(endTime).toISOString(), isOnline, ...(description ? { description } : {}), ...rel } });
    }
  };

  const title = tab === "tasks" ? "Nueva tarea" : tab === "calls" ? "Registrar llamada" : "Nueva reunión";
  return (
    <Modal open onClose={onClose} title={title}
      footer={<><Button onClick={onClose}>Cancelar</Button><Button variant="primary" onClick={submit} disabled={mut.isPending}>Guardar</Button></>}>
      <div className="space-y-4">
        <Field label={tab === "meetings" ? "Título" : "Asunto"} required><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
        {tab === "tasks" && (
          <div className="grid grid-cols-3 gap-3">
            <Field label="Vence"><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></Field>
            <Field label="Prioridad"><Select value={priority} onChange={(e) => setPriority(e.target.value)}>{PRIORITY.map((p) => <option key={p}>{p}</option>)}</Select></Field>
            <Field label="Estado"><Select value={status} onChange={(e) => setStatus(e.target.value)}>{TASK_STATUS.map((s) => <option key={s}>{s}</option>)}</Select></Field>
          </div>
        )}
        {tab === "calls" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo"><Select value={callType} onChange={(e) => setCallType(e.target.value)}>{CALL_TYPE.map((c) => <option key={c}>{c}</option>)}</Select></Field>
            <Field label="Inicio" required><Input type="datetime-local" value={startTime} onChange={(e) => setStartTime(e.target.value)} /></Field>
          </div>
        )}
        {tab === "meetings" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Inicio" required><Input type="datetime-local" value={startTime} onChange={(e) => setStartTime(e.target.value)} /></Field>
              <Field label="Fin" required><Input type="datetime-local" value={endTime} onChange={(e) => setEndTime(e.target.value)} /></Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-ink-secondary cursor-pointer">
              <input type="checkbox" className="rounded border-line" checked={isOnline} onChange={(e) => setIsOnline(e.target.checked)} />
              Reunión online — generar enlace de Microsoft Teams
            </label>
          </>
        )}
        <Field label="Relacionar con" hint="Opcional: vincula la actividad a una cuenta o contacto.">
          <div className="flex gap-2">
            <Select className="w-36 shrink-0" value={relType} onChange={(e) => { setRelType(e.target.value as "accounts" | "contacts"); setRelId(""); setRelAccountId(undefined); setRelAccountName(""); }}>
              <option value="accounts">Cuenta</option>
              <option value="contacts">Contacto</option>
            </Select>
            <div className="flex-1 min-w-0">
              <AsyncCombobox
                key={relType}
                path={relType}
                value={relId}
                labelOf={(r) => relType === "accounts" ? String(r["accountName"] ?? "") : `${String(r["firstName"] ?? "")} ${String(r["lastName"] ?? "")}`.trim() || String(r["email"] ?? "")}
                onChange={(id, row) => {
                  setRelId(id);
                  if (!id) { setRelAccountId(undefined); setRelAccountName(""); return; }
                  if (relType === "accounts") { setRelAccountId(id); setRelAccountName(String(row?.["accountName"] ?? "")); }
                  else {
                    setRelAccountId(row?.["accountId"] ? String(row["accountId"]) : undefined);
                    setRelAccountName(String(row?.["accountName"] ?? ""));
                  }
                }}
                placeholder={relType === "accounts" ? "Buscar cuenta…" : "Buscar contacto…"}
              />
            </div>
          </div>
          {relType === "contacts" && relId && (
            relAccountName
              ? <p className="mt-1.5 text-xs text-success flex items-center gap-1">✓ También se vinculará a la cuenta <span className="font-medium">{relAccountName}</span></p>
              : <p className="mt-1.5 text-xs text-ink-muted">Este contacto no tiene una cuenta asociada.</p>
          )}
        </Field>
        <Field label="Descripción"><Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}
